import { ChartOptions, TooltipItem } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import React, { useMemo, useRef, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { htmlLegendPlugin } from 'common/utils/chart';
import { formatNumberWithSeparators } from 'common/utils/functions';
import KioskChartLegends from 'extends/KioskChartLegends';

interface GenderAnalyticProps {
  gender?: {
    male: number;
    female: number;
  };
}

const KioskGenderAnalytic: React.FC<GenderAnalyticProps> = ({ gender }) => {
  const { t } = useTranslation();

  const useMemoGenderData = useMemo(() => {
    if (!gender) {
      return {
        total: [],
        gender: []
      };
    }
    const genderData = Object.values(gender);
    const total = genderData.reduce((acc, curr) => acc + curr, 0);
    return {
      total: genderData,
      gender: Object.keys(gender).map((key) => key.charAt(0).toUpperCase() + key.slice(1)),
      percentage: genderData.map((item) => ((item / total) * 100).toFixed(2)),
    };
  }, [gender]);

  const data = {
    datasets: [{
      data: useMemoGenderData.total,
      // backgroundColor: ['#8a1b5e', '#f9cf6a', '#b54419'],
      backgroundColor: ['#08415C', '#8A1B5E'],
      borderWidth: 0,
    }],
    labels: [t('system.male'), t('system.female')],
  };

  const chartRef = useRef<any>(null);
  const [hiddens, setHiddens] = useState<boolean[]>(data.labels.map(() => false));

  const options: ChartOptions<'doughnut'> = useMemo(() => ({
    responsive: true,
    cutout: '0%',
    plugins: {
      legend: {
        display: false
      },
      datalabels: {
        color: '#fff',
        font: {
          size: 16,
          weight: 600
        },
        formatter: (value: number, context) => {
          if (value === 0) return '';
          const percentage = Math.round(Number(useMemoGenderData.percentage?.[context.dataIndex]));
          // return [formatNumberWithSeparators(value), `${percentage || 0}%`];
          return [percentage > 2 ? `${percentage}%` : ''];
        }
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem: TooltipItem<'doughnut'>) => {
            const percentage = useMemoGenderData.percentage?.[tooltipItem.dataIndex];
            return `${tooltipItem.label}: ${formatNumberWithSeparators(Number(tooltipItem.raw))} (${Math.round(Number(percentage))}%)`;
          },
        },
      }
      // htmlLegend: {
      //   containerID: 'legend-container-gender',
      // },
    }
  }), [useMemoGenderData]);

  const handleLegendClick = (index: number) => {
    const chart = chartRef.current;

    if (!chart) return;

    // Toggle visibility
    chart.toggleDataVisibility(index);
    chart.update();

    // Update local state (to dim legend item)
    setHiddens((prev) => {
      const newHidden = [...prev];
      newHidden[index] = !newHidden[index];
      return newHidden;
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px',
        border: '1px solid #edeef1',
        borderRadius: '12px',
        height: '100%'
      }}
    >
      <h1
        style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#3D424F',
          marginBottom: '24px',
          width: '100%'
        }}
      >
        {t('dashboard.gender')}
      </h1>
      <div style={{ width: '200px' }}>
        <Doughnut
          ref={chartRef}
          data={data}
          options={options}
          plugins={[ChartDataLabels, htmlLegendPlugin()]}
        />
      </div>
      <KioskChartLegends
        hiddens={hiddens}
        style={{ marginTop: 20 }}
        labels={data.labels as string[]}
        colors={data.datasets[0].backgroundColor as string[]}
        onClick={handleLegendClick}
      />
    </div>
  );
};

export default KioskGenderAnalytic;
